import dispatchParsedMessage from "./DispatchParsedMessage";
import dispatchParsedHandshakeConfirm from "./DispatchParsedHandshakeConfirm";
import dispatchParsedHandshakeRequest from "./DispatchParsedHandshakeRequest";
import dispatchParsedMedia from "./DispatchParsedMedia";
import { upsertRatchetKeys } from '../../db/RatchetedConversationKeys';
import { loadLocalKeys } from '../../db/KeyStorageManager';
import { uint8ArraysEqual } from "../../utils/CryptoHelpers";
import useConversationStore from "../../stores/ConversationStore";
import { MESSAGES_TYPES } from '../../Config';

/**
 * Handles incoming messages and routes them to appropriate handlers.
 * 
 * Side effect: stores sender's ratcheting keys for secure conversations.
 * 
 * @param {Object} params
 * @param {Object} params.message - The raw message object, already parsed
 * @param {string} params.userId
 * @param {string} params.password
 * @param {string} params.salt
 */
export default async function dispatchToChat({ 
  message,
  receiverIdentityViewPublicKey,
  userId, 
  password, 
  salt, 
}) {
  const { viewPublicKey: localViewPublicKey } = await loadLocalKeys({userId, password, salt});

  const {
    senderViewPublicKey,
    senderNextViewPublicKey,
    conversationId,
    messageType,
  } = message;

  const localIsSender = uint8ArraysEqual(localViewPublicKey, senderViewPublicKey);

  // Persist latest ratcheting info from sender — ensures future encrypted communication - only if local is not the sender
  if (!localIsSender) {
    await upsertRatchetKeys({
      conversationId,
      contactIdentityViewPublicKey: senderViewPublicKey,
      contactNextViewPublicKey: senderNextViewPublicKey,
      userId,
      password,
    });
  }

  // Route message by type
  switch (messageType) {
    case MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_REQUEST:
      // Incoming handshake initiation (contact wants to connect)
      await dispatchParsedHandshakeRequest({ 
        message, 
        receiverIdentityViewPublicKey, 
        localIsSender, 
        userId, 
        password, 
        salt, 
      });
      break;

    case MESSAGES_TYPES.MESSAGE_TYPE_HANDSHAKE_CONFIRM:
      // Handshake confirmation received
      await dispatchParsedHandshakeConfirm({ 
        message, 
        receiverIdentityViewPublicKey, 
        localIsSender, 
        userId, 
        password, 
        salt,
      });
      break;

    case MESSAGES_TYPES.MESSAGE_TYPE_TEXT:
      // Encrypted 1:1 message
      await dispatchParsedMessage({ 
        conversationId, 
        receiverIdentityViewPublicKey, 
        message, 
        localIsSender, 
        userId, 
        password, 
        salt,
      });
      break;

    case 3:
      // Group message — not implemented yet
      console.warn("[dispatchToChat] Group messages not yet supported.");
      break;

    case MESSAGES_TYPES.MESSAGE_TYPE_MEDIA:
      // Encrypted 1:1 message
      await dispatchParsedMedia({ 
        conversationId, 
        receiverIdentityViewPublicKey, 
        message, 
        localIsSender, 
        userId, 
        password, 
        salt,
      });
      break;

    default:
      console.warn("[dispatchToChat] Unknown messageType:", messageType);
      break;
  }

  // update UI via zustand
  const update = useConversationStore.getState().receiveMessage;
  update({
    message,
    userId,
    password,
    salt,
  });
}